import * as React from "react";

import { connect } from "react-redux";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import { CoughWaveformType, Permission, CoughAnalysisSetsFixedCommentsDisplay } from "../../../misc/api/apiTypes";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { Authorization } from "../../../misc/components/Authorization";
import * as Actions from "../actions";
import { AnalyseState, CoughAnalysisSet, EditModeEnum, PlayStateEnum } from "../interfaces/state";
import { PeaksJsWaveform } from "../interfacesPeaks/PeaksJS";
import { ChannelToggle } from "./ChannelToggle";
import { GridlineToggle } from "./GridlineToggle";
import { PlaybackButton } from "./PlaybackButton";
import { PlayPause } from "./PlayPause";
import { ScrollActionToggle } from "./ScrollActionToggle";
import { WaveformSelector } from "./WaveformSelector";
import { SelectTimeFrameSegment } from "./SelectTimeFrameSegment";
import { ZoomSlider } from "./ZoomSlider";
import { useCoughMarshalApiClient } from "../../../misc/api/CoughMarshalApiClient";
import { PeaksPoint } from '../interfacesPeaks/PeaksJS';
import { StudyCoughEventType } from 'webportalclient';

export interface PlaybackDashboardProps {
    studyId: string;
    sessionId: string;
    isViewOnly: boolean;
    isPlaying: PlayStateEnum;
    isEditMode: EditModeEnum;
    isMicrophoneMuted: boolean;
    isSensorMuted: boolean;
    isGridlineVisible: boolean;
    isScrollActionZoom: boolean;
    selectedWaveform: string;
    zoomLevel: number;
    waveform: PeaksJsWaveform;
    areKeyboardShortcutsVisible: boolean;
    primaryAnalystSetId: string;
    selectedSegment: number;
    coughAnalysisSets: CoughAnalysisSetsFixedCommentsDisplay[];
    dispatch: any;
    pointEvents: PeaksPoint[];
    eventDuplicateThreshold: number;
    availableEventTypes: StudyCoughEventType[];
    rawTimepoints: Map<string, number>;
}

export const PlaybackDashboard = (props: PlaybackDashboardProps) => {
    const { currentUserPermissions } = useSecurityContext();
    const legacyClient = useWebPortalLegacyClient();
    const coughMarshalClient = useCoughMarshalApiClient();
    const webPortalApiClient = useWebPortalApiClient();

    return (
        <div className="row form-group">
            <div className="col-lg-2 col-md-12">
                <div className="btn-toolbar" role="toolbar">
                    <div className="btn-group" role="group">
                        <PlayPause
                            isLoading={false}
                            isPlaying={props.isPlaying}
                            isKeyboardShortcutVisible={props.areKeyboardShortcutsVisible}
                            onPlayClick={() =>
                                props.dispatch(Actions.PlayAsyncAction.create())
                            }
                            onPauseClick={() =>
                                props.dispatch(Actions.PauseAsyncAction.create())
                            }
                        />
                    </div>
                    <div className="btn-group" role="group">
                        <PlaybackButton
                            id="Reset"
                            icon="fa-step-backward"
                            shortcutKey="Home"
                            isKeyboardShortcutVisible={props.areKeyboardShortcutsVisible}
                            onClick={() => {
                                props.dispatch(Actions.PauseAction.create());
                                props.dispatch(Actions.ResetAction.create());
                            }}
                        />
                        <PlaybackButton
                            id="SkipBackward"
                            icon="fa-backward"
                            shortcutKey="←"
                            isKeyboardShortcutVisible={props.areKeyboardShortcutsVisible}
                            onClick={() =>
                                props.dispatch(Actions.SkipBackwardAction.create())
                            }
                        />
                        <PlaybackButton
                            id="SkipForward"
                            icon="fa-forward"
                            isKeyboardShortcutVisible={props.areKeyboardShortcutsVisible}
                            shortcutKey="→"
                            onClick={() =>
                                props.dispatch(Actions.SkipForwardAction.create())
                            }
                        />
                        <PlaybackButton
                            id="ToEnd"
                            icon="fa-step-forward"
                            shortcutKey="End"
                            isKeyboardShortcutVisible={props.areKeyboardShortcutsVisible}
                            onClick={() => {
                                props.dispatch(Actions.PauseAction.create());
                                props.dispatch(Actions.JumpToEndAction.create());
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="col-lg-4">
                <div className="btn-group" role="group">
                    <ChannelToggle
                        name="Microphone"
                        isMuted={props.isMicrophoneMuted}
                        onToggleMuteClick={() =>
                            props.dispatch(Actions.ToggleMuteMicrophoneAction.create())
                        }
                    />&nbsp;
                    <ChannelToggle
                        name="Sensor"
                        isMuted={props.isSensorMuted}
                        onToggleMuteClick={() =>
                            props.dispatch(Actions.ToggleMuteSensorAction.create())
                        }
                    />&nbsp;
                    <GridlineToggle
                        isVisible={props.isGridlineVisible}
                        isKeyboardShortcutVisible={props.areKeyboardShortcutsVisible}
                        onClick={() =>
                            props.dispatch(Actions.ToggleGridlineAction.create())
                        }
                    />
                </div>
                <ScrollActionToggle
                    isZoomScroll={props.isScrollActionZoom}
                    isKeyboardShortcutVisible={props.areKeyboardShortcutsVisible}
                    onToggleScrollActionClick={() =>
                        props.dispatch(Actions.ToggleScrollAction.create())
                    }
                />
            </div>
            <div className="col-lg-3 text-center">
                <div className="col-md-8">
                    <ZoomSlider
                        zoomLevel={props.zoomLevel}
                        isKeyboardShortcutVisible={props.areKeyboardShortcutsVisible}
                        onDragSlider={e =>
                            props.dispatch(Actions.SetZoomLevelAction.create(e.target.value))
                        }
                    />
                </div>
                <div className="col-md-4">
                    <SelectTimeFrameSegment
                        primaryAnalysisSetId={props.primaryAnalystSetId}
                        sessionId={props.sessionId}
                        selectedSegment={props.selectedSegment}
                        waveformSelected={props.selectedWaveform}
                        isPlaying={props.isPlaying}
                        onChange={value =>
                            props.dispatch(
                                Actions.ChangeSelectedSegmentAsyncAction.create(
                                    value,
                                    coughMarshalClient,
                                    webPortalApiClient,
                                    props.sessionId,
                                    props.primaryAnalystSetId
                                )
                            )
                        }
                        dispatch={props.dispatch}
                        availableEventTypes={props.availableEventTypes}
                        eventDuplicateThreshold={props.eventDuplicateThreshold}
                        pointEvents={props.pointEvents}
                        rawTimepoints={props.rawTimepoints}
                        isViewOnly={props.isViewOnly}
                    />
                </div>
            </div>
            <div className="col-lg-3 col-md-12 text-right">
                <div className="col-md-7">
                    <Authorization
                        currentPermissions={currentUserPermissions}
                        permissionId={PermissionEnum.CanChangeWaveform}
                        studyId={props.studyId}
                    >
                        <WaveformSelector
                            primaryAnalysisSetId={props.primaryAnalystSetId}
                            primaryAnalysisSet={props.coughAnalysisSets.find(x => x.Id === props.primaryAnalystSetId)}
                            sessionId={props.sessionId}
                            waveformSelected={props.selectedWaveform}
                            onChange={value =>
                                props.dispatch(
                                    Actions.ChangeWaveformAsyncAction.create(
                                        value,
                                        legacyClient
                                    )
                                )
                            }
                        />
                    </Authorization>
                </div>
                <div className="col-md-5">
                    <input
                        id="CurrentPosition"
                        className="form-control disabled"
                        type="text"
                        placeholder="00:00:00:000"
                        readOnly
                    />
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state: AnalyseState) {
    const waveform = state.core.peaksJs ? state.core.peaksJs.waveform : null;
    let pointEvents = state.core.peaksJs ? state.core.peaksJs.points.getPoints() as Array<PeaksPoint> : [] as Array<PeaksPoint>;

    return {
        studyId: state.core.studyId,
        sessionId: state.core.sessionMetadata.sessionId,
        isViewOnly: state.core.isViewOnly,
        isPlaying: state.core.playState,
        isEditMode: state.core.isEditMode,
        isMicrophoneMuted: state.core.isMicrophoneMuted,
        isSensorMuted: state.core.isSensorMuted,
        isGridlineVisible: state.core.isGridlineVisible,
        isScrollActionZoom: state.core.isScrollActionZoom,
        zoomLevel: state.core.zoomLevel,
        selectedWaveform: state.core.selectedWaveform,
        selectedSegment: state.core.selectedSegment,
        areKeyboardShortcutsVisible: state.core.areKeyboardShortcutsVisible,
        pointEvents: pointEvents,
        rawTimepoints: state.core.rawTimestampPoints,
        eventDuplicateThreshold: state.core.eventDuplicateThreshold,
        availableEventTypes: state.core.availableEventTypes,
        waveform,
        primaryAnalystSetId: state.analysisSet.primaryAnalystSetId,
        coughAnalysisSets: state.analysisSet.coughAnalysisSets
    } as PlaybackDashboardProps;
}

export default connect<PlaybackDashboardProps, any>(mapStateToProps)(PlaybackDashboard);
